import { PayloadAction, createAction, createSlice } from "@reduxjs/toolkit";
import { cloneDeep, isNumber } from "lodash";
import { Preferences } from "../../profile/objects/preferences";
import { applyColumnResize, ColumnResizePayload } from '../../utils/list/utils/columnResize';
import { EMPTY_SETTLEMENT_LIST_CONFIGURATION, EMPTY_SETTLEMENT_LIST_CRITERIA } from "../constant/settlementConstant";
import { Settlement } from "../objects/settlement";
import { SettlementListCriteria } from "../objects/settlementListCriteria";
import { SettlementState } from "../objects/settlementState";
import { setSettlementPreference } from "../utils/setSettlementPreference";
import { Column } from "../objects/column";


const initialState: SettlementState = {
    settlement_list: [],
    settlement_list_configuration: EMPTY_SETTLEMENT_LIST_CONFIGURATION,
    settlement_list_loading: false,
    settlement_list_count: 0,
    settlement_list_criteria: EMPTY_SETTLEMENT_LIST_CRITERIA,
    settlement: null,
    settlement_delete: null,
    first_preference_loaded: false,
};

export const setPreferences = createAction<Preferences[]>('PROFILE_SET_PREFERENCES');

const addSettlementListConfigurationPreferenceReducer = (state: SettlementState, action: PayloadAction<Preferences[]>): SettlementState => ({
    ...state, settlement_list_configuration: { ...state.settlement_list_configuration, preferences: [...state.settlement_list_configuration.preferences, ...action.payload] }
});

const setSettlementListConfigurationPreferenceReducer = (state: SettlementState, action: PayloadAction<Preferences[]>): SettlementState => ({
    ...state, settlement_list_configuration: { ...state.settlement_list_configuration, preferences: action.payload }
});


const setPreferenceReducer = (state: SettlementState, action: PayloadAction<Preferences[]>): SettlementState => {
    const { preferences, columns, order_by, order } = setSettlementPreference(action.payload, state.settlement_list_configuration.columns);
    return {
        ...state,
        first_preference_loaded: true,
        settlement_list_configuration: { ...state.settlement_list_configuration, preferences, columns },
        settlement_list_criteria: { ...state.settlement_list_criteria, order, order_by }
    };
};

const setColumnsReducer = (state: SettlementState, action: PayloadAction<Column[]>): SettlementState => ({
    ...state, settlement_list_configuration: { ...state.settlement_list_configuration, columns: action.payload }
});

const toggleColumnReducer = (state: SettlementState, action: PayloadAction<number>): SettlementState => {
    const columns = cloneDeep([...state.settlement_list_configuration.columns]);
    const column = { ...columns[action.payload] };
    column.checked = !column.checked;
    columns[action.payload] = column;
    return { ...state, settlement_list_configuration: { ...state.settlement_list_configuration, columns } };
};

const updateSettlementListReducer = (state: SettlementState, action: PayloadAction<Settlement[]>): SettlementState => ({
    ...state, settlement_list: action.payload
});
const updateSettlementListCriteriaReducer = (state: SettlementState, action: PayloadAction<SettlementListCriteria>): SettlementState => ({
    ...state, settlement_list_criteria: action.payload
});
const updateSettlementReducer = (state: SettlementState, action: PayloadAction<Settlement | null>): SettlementState => ({
    ...state,
    settlement: action.payload ? { ...action.payload, day_of_the_week: isNumber(action.payload.day_of_the_week) ? action.payload.day_of_the_week : -1, custom_date: action.payload.custom_date? new Date(action.payload.custom_date): null } : null,
});
const updateSettlementListLoadingReducer = (state: SettlementState, action: PayloadAction<boolean>): SettlementState => ({
    ...state, settlement_list_loading: action.payload
});
const updateSettlementListCountReducer = (state: SettlementState, action: PayloadAction<number>): SettlementState => ({
    ...state, settlement_list_count: action.payload
});
const updateOneSettlementListReducer = (state: SettlementState, action: PayloadAction<Settlement>): SettlementState => ({
    ...state, settlement_list: state.settlement_list.map((settlement) => settlement.id === action.payload.id ? ({ ...action.payload, day_of_the_week: isNumber(action.payload.day_of_the_week) ? action.payload.day_of_the_week : -1 }): settlement)
});
const setSettlementDeleteReducer = (state: SettlementState, action: PayloadAction<null | Settlement>): SettlementState => ({
    ...state, settlement_delete: action.payload
});

const resizeColumnReducer = (state: SettlementState, action: PayloadAction<ColumnResizePayload>): void => {
    state.settlement_list_configuration.columns = applyColumnResize(state.settlement_list_configuration.columns, action.payload);
};

export const settlement = createSlice({
    name: 'settlement',
    initialState,
    reducers: {
        addSettlementListConfigurationPreference: addSettlementListConfigurationPreferenceReducer,
        setSettlementListConfigurationPreference: setSettlementListConfigurationPreferenceReducer,
        setColumns: setColumnsReducer,
        toggleColumn: toggleColumnReducer,
        updateSettlementList: updateSettlementListReducer,
        updateSettlementListCriteria: updateSettlementListCriteriaReducer,
        updateSettlement: updateSettlementReducer,
        updateSettlementListLoading: updateSettlementListLoadingReducer,
        updateSettlementListCount: updateSettlementListCountReducer,
        updateOneSettlementList: updateOneSettlementListReducer,
        setSettlementDelete: setSettlementDeleteReducer,
        resizeColumn: resizeColumnReducer
    },
    extraReducers: (builder) => {
        builder
            .addCase(setPreferences, setPreferenceReducer);
    }
});

export const {
    addSettlementListConfigurationPreference,
    setSettlementListConfigurationPreference,
    setColumns,
    toggleColumn,
    updateSettlementList,
    updateSettlementListCriteria,
    updateSettlement,
    updateSettlementListLoading,
    updateSettlementListCount,
    updateOneSettlementList,
    setSettlementDelete,
    resizeColumn
} = settlement.actions;